import { useState } from "react"

import { useTranslation } from "react-i18next"

import { ClipboardCheck } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"

import { HostedAgentEvalCasesPanel } from "./hosted-agent-eval-cases-panel"
import { HostedAgentEvalRunHistory } from "./hosted-agent-eval-run-history"
import { HostedAgentEvalRunPanel } from "./hosted-agent-eval-run-panel"
import { useAdminHostedAgentEvalCasesQuery } from "../hooks/admin-queries"

// Evals tab of the hosted-agent edit wizard (#3028).
//
// Three surfaces, one agent: the exam (cases), the measurement (a run that is
// started with a 202 and then polled), and the record (previous runs with
// their provenance). It sits next to Test on purpose — both hold the same
// server-side single-flight, so each panel explains the other's 409 — but it
// is its own tab because cases are persistent objects edited without running
// anything, and because the three surfaces would never fit one file.
//
// Like Routines and Lifecycle, the tab needs a saved agent: there is no id to
// hang cases or runs off before the first save.

export function HostedAgentEvalsTab({ agentId }: { agentId: string | undefined }) {
  const { t } = useTranslation()
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([])
  const casesQuery = useAdminHostedAgentEvalCasesQuery(agentId)
  const cases = casesQuery.data ?? []
  const enabledCases = cases.filter((source) => source.enabled !== false)

  if (!agentId) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.hostedAgents.evals.saveFirst", {
          defaultValue: "Save the agent before adding eval cases.",
        })}
      </p>
    )
  }

  const toggleCase = (caseId: string) => {
    setSelectedCaseIds((current) =>
      current.includes(caseId)
        ? current.filter((candidate) => candidate !== caseId)
        : [...current, caseId]
    )
  }

  // The server runs enabled cases only. A selection that still holds a case
  // disabled after it was ticked would otherwise promise more than it runs —
  // or, if every pick is disabled, earn a 400 from a button that looked ready.
  const enabledIds = new Set(enabledCases.map((source) => source.id))
  const runnableCaseIds = selectedCaseIds.filter((caseId) => enabledIds.has(caseId))

  return (
    <div className="space-y-4">
      <Alert>
        <ClipboardCheck className="size-4" />
        <AlertTitle>
          {t("adminDashboard.hostedAgents.evals.title", { defaultValue: "Eval suite" })}
        </AlertTitle>
        <AlertDescription>
          {t("adminDashboard.hostedAgents.evals.intro", {
            defaultValue:
              "A suite of fixed prompts with expectations about what the agent must do, run through the real execution path. Results are three-valued: passed, failed, or could not be measured — the last is never counted as a pass, so a score is only as good as its evidence coverage.",
          })}
        </AlertDescription>
      </Alert>

      <HostedAgentEvalCasesPanel
        agentId={agentId}
        cases={cases}
        isError={casesQuery.isError}
        isLoading={casesQuery.isLoading}
        selectedCaseIds={selectedCaseIds}
        onClearSelection={() => setSelectedCaseIds([])}
        onToggleCase={toggleCase}
      />

      <div className="space-y-3 border-t pt-3">
        <p className="text-sm font-medium">
          {t("adminDashboard.hostedAgents.evals.run.title", { defaultValue: "Run the suite" })}
        </p>
        <HostedAgentEvalRunPanel
          agentId={agentId}
          hasNoCases={enabledCases.length === 0}
          selectedCaseIds={runnableCaseIds}
          totalCases={enabledCases.length}
        />
      </div>

      <HostedAgentEvalRunHistory agentId={agentId} />
    </div>
  )
}
